'use client'

import { useState } from "react";
import { useForm } from "react-hook-form";


interface ContactFormNonAnimatedProps {
  data?: any
}

type FormData = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  birthDate: string;
  timeframe: string;
  message: string;
  consent: boolean;
  city: string; // Honeypot
};

export default function ContactFormNonAnimated({ data }: ContactFormNonAnimatedProps) {
  const [formError, setFormError] = useState<string>("");
  const [formSuccess, setFormSuccess] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    mode: "onBlur",
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setFormError("");
    setFormSuccess("");

    try {
      const response = await fetch('/api/contact-form/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, js_enabled: '1' }),
      });

      if (!response.ok) throw new Error();
      setFormSuccess("Thank you for your message. We'll be in touch soon!");
    } catch (error) {
      setFormError("Failed to submit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="xl:px-16 py-8 xl:py-16 w-full top-0 left-0 bg-[#f1f6fb]">
      <div className="relative w-full flex items-center">
        {/* Background Circle */}
        <div className="gl-container relative flex flex-col gap-8 xl:gap-16 xl:max-w-screen-2xl xl:mx-auto">
          {/* Heading */}

          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-left md:text-center mb-12 lg:mb-20 text-primary">
            {data?.title || "Interested in Finding Out More? Tell Us Your Story"}
          </h2>

          {formError && (
            <div className="text-red-500 text-center font-greycliff">{formError}</div>
          )}
          
          {formSuccess ? (
            <div className="text-primary text-center font-greycliff text-xl py-8">{formSuccess}</div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 xl:gap-8 xl:grid xl:grid-cols-2 xl:gap-x-16 xl:gap-y-10 ">
              {/* Honeypot field */}
              <input type="hidden" {...register('city')} />
              
              {/* First Name */}
              <div className="form-field group relative">
                <input
                  type="text"
                  {...register('firstName', { required: true })}
                  placeholder="First Name"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
                {errors.firstName && <span className="text-red-500 text-xs">Required</span>}
              </div>

              {/* Last Name */}
              <div className="form-field group relative">
                <input
                  type="text"
                  {...register('lastName', { required: true })}
                  placeholder="Last Name"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
                {errors.lastName && <span className="text-red-500 text-xs">Required</span>}
              </div>

              {/* Phone */}
              <div className="form-field group relative">
                <input
                  type="tel"
                  {...register('phone')}
                  placeholder="Phone Number"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
              </div>

              {/* Email */}
              <div className="form-field group relative">
                <input
                  type="email"
                  {...register('email', { 
                    required: true,
                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                  })}
                  placeholder="E-mail"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
                {errors.email && <span className="text-red-500 text-xs">Valid email required</span>}
              </div>

              {/* Birth Date */}
              <div className="form-field group relative">
                <input
                  type="text"
                  {...register('birthDate', {
                    pattern: {
                      value: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
                      message: "Please enter date in MM/DD/YYYY format"
                    }
                  })}
                  placeholder="Birth Date (MM/DD/YYYY)"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
                {errors.birthDate && <span className="text-red-500 text-xs">{errors.birthDate.message}</span>}
              </div>

              {/* Timeframe */}
              <div className="form-field group relative">
                <select
                  {...register('timeframe')}
                  defaultValue=""
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none appearance-none placeholder:text-primary"
                >
                  <option value="" disabled>
                    What's Your Timeframe?
                  </option>
                  <option value="1-3months">1-3 months</option>
                  <option value="3-6months">3-6 months</option>
                  <option value="6-12months">6-12 months</option>
                </select>
                <div className="h-px border-t border-[#94a4bb] mt-0.5 transition-colors focus-within:border-primary" />
              </div>

              {/* Message - Full Width */}
              <div className="col-span-2 relative">
                <textarea
                  {...register('message')}
                  rows={4}
                  placeholder="Message"
                  className="w-full bg-transparent text-base font-light font-greycliff leading-tight tracking-tight text-primary xl:text-xl focus:outline-none resize-none placeholder:text-primary"
                />
                <div className="h-px border-t border-[#94a4bb] transition-colors focus-within:border-primary" />
              </div>

              {/* Consent Checkbox */}
              <div className="col-span-2 flex gap-2 items-start mt-2 xl:mt-4">
                <input
                  type="checkbox"
                  {...register('consent', { required: true })}
                  className="mt-0.5 w-4 h-4 border border-primary rounded-none focus:ring-0 focus:ring-offset-0"
                />
                <label
                  htmlFor="consent"
                  className="text-xs font-light font-greycliff leading-3 tracking-tight text-primary"
                >
                  I agree to receive text messages about my healthcare and for
                  marketing purposes, including auto dialed, from Bader Facial
                  Plastics. Plastic Surgery. I understand that I can opt out at
                  any time, message/data rates may apply per my phone plan, and
                  opting-in includes acceptance of our Privacy Policy and Terms of
                  Service.
                </label>
              </div>
              {errors.consent && <span className="col-span-2 text-red-500 text-xs">Consent is required</span>}

              {/* Submit Button */}
              <div className="col-span-2 flex justify-end mt-4 xl:mt-8">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full xl:w-auto px-12 py-2.5 bg-primary rounded-3xl text-white text-xl font-normal font-greycliff leading-7 tracking-tight hover:bg-secondary transition-colors disabled:opacity-70"
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

